import * as React from "react";
import { useQuery } from "react-query";
import { DataTable } from "components/DataTable/DataTable";
import { ApiErrorMessage } from "components/ApiErrorMessage/ApiErrorMessage";
import { TablePagination, Stack } from "@mui/material";
import { LqaRatingAnalyticsReportItem, ReportFileInfo, MetricsService } from "gen/clients";
import { formatDate } from "utils/dateUtils";

interface Props {
  reportId: string;
  jsonFile: ReportFileInfo;
}

const LqaRatingReportTable: React.FC<Props> = ({ reportId, jsonFile }) => {
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);

  const {
    data: reportData,
    isLoading,
    error
  } = useQuery<LqaRatingAnalyticsReportItem[]>(["lqaRatingReportData", reportId, jsonFile.s3Key], {
    queryFn: async () => {
      if (!jsonFile.s3Key) {
        throw new Error("JSON file s3Key is missing");
      }

      const downloadResponse = await MetricsService.downloadReportJobFile({
        reportId,
        s3Key: encodeURIComponent(jsonFile.s3Key)
      });

      const response = await fetch(downloadResponse.downloadUrl);
      if (!response.ok) {
        throw new Error(`Failed to fetch report data: ${response.statusText}`);
      }
      return response.json();
    },
    enabled: !!jsonFile.s3Key,
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000
  });

  const handleChangePage = React.useCallback((event: unknown, newPage: number) => {
    setPage(newPage);
  }, []);

  const handleChangeRowsPerPage = React.useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  }, []);

  const displayedData = React.useMemo(() => {
    if (!reportData) {
      return [];
    }
    return reportData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  }, [reportData, page, rowsPerPage]);

  return (
    <Stack>
      <DataTable
        isLoading={isLoading}
        error={!!error && <ApiErrorMessage apiError={error} />}
        columns={[
          {
            id: "username",
            title: "User name",
            cell: (row: LqaRatingAnalyticsReportItem) => row.username
          },
          {
            id: "fileName",
            title: "File name",
            cell: (row: LqaRatingAnalyticsReportItem) => row.fileName
          },
          {
            id: "sourceLanguage",
            title: "Source Language",
            cell: (row: LqaRatingAnalyticsReportItem) => row.sourceLanguage
          },
          {
            id: "targetLanguage",
            title: "Target Language",
            cell: (row: LqaRatingAnalyticsReportItem) => row.targetLanguage
          },
          {
            id: "rating",
            title: "Rating",
            cell: (row: LqaRatingAnalyticsReportItem) => row.rating
          },
          {
            id: "dateTime",
            title: "Date/Time",
            cell: (row: LqaRatingAnalyticsReportItem) => formatDate(row.dateTime)
          }
        ]}
        data={displayedData}
        rowKey={(row: LqaRatingAnalyticsReportItem) => `${row.username}-${row.fileName}-${row.dateTime}`}
      />
      <TablePagination
        rowsPerPageOptions={[10, 20, 50, 100]}
        component="div"
        count={reportData?.length || 0}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </Stack>
  );
};

export { LqaRatingReportTable };
